import { RouteNames } from '@/data/routes';
import { COUNTRY_CODES } from '@/data/country-code-with-names';
import i18n from '@/plugins/i18n';
import { CountryCode } from '@clearbanc/data-common-types';
import { RouteLocationRaw } from 'vue-router';

type TranslationFn = () => string;

interface StepSubtitle2 {
  title: string;
  link: string;
  href: string;
}

interface PaymentsStep {
  name: string;
  nextRoute?: RouteLocationRaw;
  title: TranslationFn;
  subtitle: TranslationFn;
  subtitle2?: () => StepSubtitle2;
  ctaText?: string;
  redirectRoute?: RouteLocationRaw;
  complete: boolean;
  skippable: boolean;
  firstStepOfForm?: { name: string };
  formCompleteStep?: { name: string };
}

type PaymentsSteps = Record<string, PaymentsStep>;

interface PaymentsConfig {
  [key: string]: {
    steps: PaymentsSteps;
  };
}

type PaymentsActions = {
  [key: string]: Record<string, string>;
};

interface CurrencyOption {
  symbol: string;
  name: string;
  symbol_native: string;
  decimal_digits: number;
  rounding: number;
  code: string;
  name_plural: string;
}

type PayOutCurrencyOptions = Record<string, CurrencyOption>;

interface RevenueBand {
  name: () => string;
  value: string;
}

export interface BannerMessage {
  title: string;
  paragraph1: string;
  paragraph2?: string;
  paragraph3?: string;
  linkText?: string;
  linkUrl?: string;
  p2LinkText?: string;
  paragraph2NoLink?: string;
  supportEmail?: string;
}

type UserCategoryBannerMessages = Partial<
  Record<CountryCode | 'INTERNATIONAL', BannerMessage>
>;

type WaitlistBannerMessaging = Record<string, UserCategoryBannerMessages>;

export const PAYMENTS_STATE_MODULE = 'payments-module';
export const MAX_INVOICE_DUE_DATE_IN_MONTHS = 2;

// analytics
export const PAYMENTS_EXPERIMENT_NAME = 'ClearPayments';

export enum ScheduledTransactionType {
  PAYMENT = 'payment',
  VENDOR_INVOICE = 'vendor_invoice',
}

export enum TransactionPlatform {
  CURRENCY_CLOUD = 'currency_cloud',
}

export enum TransactionStatus {
  PENDING = 'pending',
  IN_TRANSIT = 'in_transit',
  FAILED = 'failed',
  SUCCESS = 'success',
  CANCELED = 'canceled',
  SETTLED = 'settled',
  SCHEDULED = 'scheduled',
  SUBMITTED = 'submitted',
  DRAFT = 'draft',
}

export enum TransactionStatusCode {
  NO_USABLE_GATEWAY = 'no_usable_gateway',
  INSUFFICIENT_FUNDS = 'ach_R01',
  ACCOUNT_CLOSED = 'ach_R02',
  ACCOUNT_NOT_FOUND = 'ach_R03',
  INVALID_ACCOUNT_NUMBER = 'ach_R04',
  UNAUTHORIZED_DEBIT = 'ach_R05',
  AUTHORIZATION_REVOKED = 'ach_R07',
  PAYMENT_STOPPED = 'ach_R08',
  UNCOLLECTED_FUNDS = 'ach_R09',
  ACCOUNT_FROZEN = 'ach_R16',
  NON_TRANSACTION_AMOUNT = 'ach_R20',
  CORPORATE_CUSTOMER_ADVISES_UNAUTHORIZED = 'ach_R29',
  COMPLIANCE = 'compliance',
}

// Categorization determines the error message shown to the user
export const CATEGORIZED_TRANSACTION_ERROR_CODES = {
  GATEWAY: [TransactionStatusCode.NO_USABLE_GATEWAY],
  DIRECT_DEBIT: [
    TransactionStatusCode.PAYMENT_STOPPED,
    TransactionStatusCode.UNAUTHORIZED_DEBIT,
    TransactionStatusCode.CORPORATE_CUSTOMER_ADVISES_UNAUTHORIZED,
    TransactionStatusCode.AUTHORIZATION_REVOKED,
  ],
  NSF: [
    TransactionStatusCode.INSUFFICIENT_FUNDS,
    TransactionStatusCode.UNCOLLECTED_FUNDS,
  ],
  ACCOUNT_NUMBER: [
    TransactionStatusCode.INVALID_ACCOUNT_NUMBER,
    TransactionStatusCode.ACCOUNT_NOT_FOUND,
    TransactionStatusCode.ACCOUNT_CLOSED,
  ],
};

/*
Configuration to simplify things for customers
pending, submitted, in_transit -> Pending
scheduled -> Scheduled
failed -> Failed
success, settled -> Success
canceled -> Canceled
*/
export const TransactionStatusLabel: Record<TransactionStatus, string> = {
  [TransactionStatus.PENDING]: i18n.t(
    'data.payments.transactionStatusLabels.pending',
  ),
  [TransactionStatus.IN_TRANSIT]: i18n.t(
    'data.payments.transactionStatusLabels.pending',
  ),
  [TransactionStatus.SUBMITTED]: i18n.t(
    'data.payments.transactionStatusLabels.pending',
  ),
  [TransactionStatus.SCHEDULED]: i18n.t(
    'data.payments.transactionStatusLabels.scheduled',
  ),
  [TransactionStatus.FAILED]: i18n.t(
    'data.payments.transactionStatusLabels.failed',
  ),
  [TransactionStatus.SUCCESS]: i18n.t(
    'data.payments.transactionStatusLabels.success',
  ),
  [TransactionStatus.SETTLED]: i18n.t(
    'data.payments.transactionStatusLabels.success',
  ),
  [TransactionStatus.CANCELED]: i18n.t(
    'data.payments.transactionStatusLabels.canceled',
  ),
  [TransactionStatus.DRAFT]: i18n.t(
    'data.payments.transactionStatusLabels.draft',
  ),
};

export enum BillStatus {
  UNREAD = 'unread',
  PROCESSING = 'processing',
  APPROVING_VENDOR = 'approving_vendor',
  WAITING_ON_FOUNDER = 'waiting_on_founder',
  WAITING_ON_CLEARCO = 'waiting_on_clearco',
  COMPLETED = 'completed',
  ERROR = 'error',
  CANCELLED = 'cancelled',
  SCHEDULED = 'scheduled',
  PAYING_VENDOR = 'paying_vendor',
  WAITING_FOR_PAYMENT = 'waiting_for_payment',
  BNPL_APPROVED = 'bnpl_approved',
  BNPL_DECLINED = 'bnpl_declined',
  AUTO_DEBITING_ACTIVE = 'auto_debiting_active',
}

export const TransactionToBillStatus: Record<TransactionStatus, BillStatus> = {
  [TransactionStatus.SUCCESS]: BillStatus.COMPLETED,
  [TransactionStatus.FAILED]: BillStatus.ERROR,
  [TransactionStatus.CANCELED]: BillStatus.CANCELLED,
  [TransactionStatus.IN_TRANSIT]: BillStatus.PAYING_VENDOR,
  [TransactionStatus.PENDING]: BillStatus.SCHEDULED,
  [TransactionStatus.DRAFT]: BillStatus.PROCESSING,
  [TransactionStatus.SETTLED]: BillStatus.SCHEDULED,
  [TransactionStatus.SCHEDULED]: BillStatus.SCHEDULED,
  [TransactionStatus.SUBMITTED]: BillStatus.PROCESSING,
};

export const BillLabel: Record<BillStatus, string> = {
  [BillStatus.UNREAD]: i18n.t('data.payments.billStatus.unread'),
  [BillStatus.PROCESSING]: i18n.t('data.payments.billStatus.processing'),
  [BillStatus.APPROVING_VENDOR]: i18n.t('data.payments.billStatus.approving'),
  [BillStatus.WAITING_ON_FOUNDER]: i18n.t(
    'data.payments.billStatus.waitingFounder',
  ),
  [BillStatus.WAITING_ON_CLEARCO]: i18n.t(
    'data.payments.billStatus.waitingClearco',
  ),
  [BillStatus.COMPLETED]: i18n.t('data.payments.billStatus.success'),
  [BillStatus.ERROR]: i18n.t('data.payments.billStatus.error'),
  [BillStatus.CANCELLED]: i18n.t('data.payments.billStatus.cancelled'),
  [BillStatus.SCHEDULED]: i18n.t('data.payments.billStatus.scheduled'),
  [BillStatus.PAYING_VENDOR]: i18n.t('paymentsPage.pending'),
  [BillStatus.WAITING_FOR_PAYMENT]: i18n.t(
    'data.payments.billStatus.waitingPayment',
  ),
  [BillStatus.BNPL_APPROVED]: i18n.t('data.payments.billStatus.approved'),
  [BillStatus.BNPL_DECLINED]: i18n.t('data.payments.billStatus.declined'),
  [BillStatus.AUTO_DEBITING_ACTIVE]: i18n.t(
    'data.payments.billStatus.autoDebitingActive',
  ),
};

export enum BillStatusLabel {
  CANCELLED = 'Cancelled',
  DECLINED = 'Declined',
  PROCESSING = 'Processing',
  SCHEDULED = 'Scheduled',
  ACTION_REQUIRED = 'Action Required',
  PAID = 'Paid',
}

export enum BillPaymentMethods {
  CREDIT_CARD = 'credit_card',
  ADVANCE = 'advance',
  BANK_ACCOUNT = 'bank_account',
  BNPL = 'bnpl',
}

export enum BillPaymentFees {
  FREE = 'free',
  CREDIT_CARD_FEES = '2.9%',
}

// TODO: i18n
export const BillPaymentLabels: Record<BillPaymentMethods, string> = {
  [BillPaymentMethods.CREDIT_CARD]: 'Credit Card',
  [BillPaymentMethods.ADVANCE]: 'Clearco Advance',
  [BillPaymentMethods.BANK_ACCOUNT]: 'Bank Account',
  [BillPaymentMethods.BNPL]: 'Extension Plan',
};

// TODO: i18n
export const BillStatusTooltipMessages: Partial<Record<BillStatus, string>> = {
  [BillStatus.PROCESSING]:
    'We are processing your invoice. No action for you to take. We will email you if we need to.',
  [BillStatus.APPROVING_VENDOR]:
    'We are currently verifying the vendor you are trying to pay. Typically takes 1 business day.',
  [BillStatus.WAITING_ON_FOUNDER]:
    'We are waiting for you to take an action. Please read the message below for details.',
  [BillStatus.WAITING_ON_CLEARCO]:
    'We are currently waiting on an internal process to finish.',
  [BillStatus.COMPLETED]: 'Your invoice has been successfully paid.',
  [BillStatus.ERROR]:
    'There is a problem processing your invoice. Please see the message for more details.',
  [BillStatus.CANCELLED]: 'Your invoice payment has been cancelled.',
  [BillStatus.SCHEDULED]: ' Your payment has been scheduled.',
  [BillStatus.PAYING_VENDOR]: 'We are currently paying your vendor.',
  [BillStatus.WAITING_FOR_PAYMENT]:
    'We are waiting for you to pay a balance on your Clear Payments account.',
  [BillStatus.BNPL_APPROVED]: 'Your invoice payment has been approved',
  [BillStatus.BNPL_DECLINED]: 'Your invoice payment has been declined',
  [BillStatus.AUTO_DEBITING_ACTIVE]:
    'Your invoice has been paid and is currently being debitted',
};

enum DILIGENCE_DECLINED_REASONS {
  CASE_CREATED_IN_ERROR = 'Case Created in Error',
  BILL_DENIED_INVALID_FORMAT = 'Bill Denied (Invalid Format)',
  BILL_DENIED_INVALID_AMOUNT = 'Bill Denied (Invalid Amount)',
  BILL_DENIED_PASSED_DUE = 'Bill Denied (Passed Due)',
  DUPLICATED = 'Duplicate',
  EXPIRED = 'Expired',
  INCOMPLETE_INFORMATION = 'Incomplete Information',
  COMPLIANCE_RISK_AML = 'Compliance Risk | AML',
  COMPLIANCE_RISK_KYB = 'Compliance Risk | KYB',
  COMPLIANCE_RISK_KYC = 'Compliance Risk | KYC',
  COMPLIANCE_RISK_PROHIBITED_INDUSTRY = 'Compliance Risk | Prohibited Industry',
  CONCENTRATION_RISK_MONTHY_LIMIT = 'Concentration Risk | Monthly Limit',
  CONCENTRATION_RISK_PORTIFOLIO_LIMIT = 'Concentration Risk | Portfolio Limit',
  FINANCIAL_STATEMENTS_ERROR = 'Financial Statements Error',
  FINANCIAL_STATEMENTS_INCOMPLETE = 'Financial Statements Incomplete',
  FINANCIAL_STATEMENTS_UNAVAILABLE = 'Financial Statements Unavailable',
  INCOME_RISK_GROSS_PROFIT_MARGIN = 'Income Risk | Gross Profit Margin',
  INCOME_RISK_MARKETING_EXPENSE_TO_REVENUE = 'Income Risk | Marketing Expense to Revenue',
  INCOME_RISK_NET_INCOME_MARGIN = 'Income Risk | Net Income Margin',
  INCOME_RISK_REVENUE_DECLINE = 'Income Risk | Revenue Decline',
  INTERNAL_BUSINESS_RISK_HOSTILE_TAG = 'Internal Business Risk | Hostile Tag',
  INTERNAL_BUSINESS_RISK_ICEBOX_TAG = 'Internal Business Risk | Icebox Tag',
  LIQUIDITY_RISK_CURRENT_RATIO = 'Liquidity Risk | Current Ratio',
  LIQUIDITY_RISK_DEBT_SERVICE_RATIO = 'Liquidity Risk | Debt Service Ratio',
  LIQUIDITY_RISK_DEBT_TO_ASSETS = 'Liquidity Risk | Debt to Assets',
  LIQUIDITY_RISK_QUICK_RATIO = 'Liquidity Risk | Quick Ratio',
  SALES_LOST = 'Sales Lost',
  TECH_BLOCKER_OTHER = ' Tech Blocker | Other',
  TECH_BLOCKER_PLAID_CONNECTION = 'Tech Blocker | Plaid Connection',
  TECH_BLOCKER_REVENUE_CONNECTION = 'Tech Blocker | Revenue Connection',
  INVOICE_AMOUNT_EXCEEDS_MAX_OFFER = 'Invoice Amount Exceeds Max Offer',
  VENDOR_DENIED_KYB_FAILED = 'Vendor Denied (KYB Failed)',
  VENDOR_DENIED_UNSUPPORTED_COUNTRY_OR_CURRENCY = 'Vendor Denied (Unsupported Country or Currency)',
  VENDOR_DENIED_INVALID_FORMAT = 'Vendor Denied (Invalid Format)',
  VENDOR_DENIED_INVALID_AMOUNT = 'Vendor Denied (Invalid Amount)',
  VENDOR_DENIED_VENDOR_DENIED = 'Vendor Denied (Passed Due)',
  VENDOR_DENIED_UNSUPPORTED_SPEND_CATEGORY = 'Vendor Denied (Unsupported Spend Category)',
  VENDOR_DENIED_UNSUPPORTED_VENDOR_FOR_RECEIPT = 'Vendor Denied (Unsupported Vendor for Receipt)',
  SHOULD_BE_AN_INVOICE = 'Should be an Invoice',
  SHOULD_BE_A_RECEIPT = 'Should be a Receipt',
  OVER_100K_30_DAYS = 'Over 500K per 30 days limit',
  OTHER = 'Other',
}

export const VENDOR_DENIED_REASONS = [
  DILIGENCE_DECLINED_REASONS.VENDOR_DENIED_KYB_FAILED,
  DILIGENCE_DECLINED_REASONS.VENDOR_DENIED_UNSUPPORTED_COUNTRY_OR_CURRENCY,
  DILIGENCE_DECLINED_REASONS.VENDOR_DENIED_INVALID_FORMAT,
  DILIGENCE_DECLINED_REASONS.VENDOR_DENIED_INVALID_AMOUNT,
  DILIGENCE_DECLINED_REASONS.VENDOR_DENIED_VENDOR_DENIED,
  DILIGENCE_DECLINED_REASONS.VENDOR_DENIED_UNSUPPORTED_SPEND_CATEGORY,
  DILIGENCE_DECLINED_REASONS.VENDOR_DENIED_UNSUPPORTED_VENDOR_FOR_RECEIPT,
];

export const isVendorDenied = (status: DILIGENCE_DECLINED_REASONS) => {
  return VENDOR_DENIED_REASONS.includes(status);
};

export const BillDeclinedDescription = {
  [DILIGENCE_DECLINED_REASONS.OVER_100K_30_DAYS]: `The maximum receipt funding submission amount is your maximum Funding Capacity up to an upper limit of $200,000 USD per 30 day period.`,
  [DILIGENCE_DECLINED_REASONS.INVOICE_AMOUNT_EXCEEDS_MAX_OFFER]: `The invoice amount submitted exceeds the maximum Funding Capacity available.`,
  [DILIGENCE_DECLINED_REASONS.VENDOR_DENIED_KYB_FAILED]: `The selected vendor does not meet our compliance criteria.`,
};

// TODO: i18n
export enum messageToFounderTitles {
  completed = 'Payment complete',
  waiting_on_founder = 'Action Required',
  error = 'Error',
}

// List of tracked CTAs for ClearPay
export enum CLEARPAY_CTA_SOURCES {
  paymentsList = 'payments-list',
  onboardingBanner = 'onboarding-banner',
  repaymentsPage = 'repayments-page',
  userCapacity = 'user-capacity',
  joinWaitlistBanner = 'join-waitlist-banner',
}

export const HELP_CENTER_URL = 'https://clear.co/faq/';

export const CANCEL_WINDOW_DAYS = 3;

export const CANCELLATION_NOT_WITHIN_WINDOW =
  'Cancellation request NOT within their cancellation window';

export const BNPL_SCHEDULE_RANGE = [
  { value: 1, label: 'month' },
  { value: 2, label: 'months' },
  { value: 3, label: 'months' },
  { value: 4, label: 'months' },
  { value: 5, label: 'months' },
  { value: 6, label: 'months' },
];

export const PAYMENTS_GETTERS = {
  [PAYMENTS_STATE_MODULE]: {
    experimentName: 'experimentName',
    featureFlags: 'featureFlags',
    firstIncompleteStep: 'firstIncompletePaymentsOnboardingStep',
    steps: 'steps',
  },
};

export const PAYMENTS_STEPS: PaymentsSteps = {
  [RouteNames.BUSINESS.INDEX]: {
    name: RouteNames.ONBOARDING.BUSINESS,
    nextRoute: {
      name: RouteNames.PROFILE.INDEX,
    },
    title: () => i18n.t('data.onboarding.steps.businessDetails.title'),
    subtitle: () => i18n.t('data.onboarding.steps.businessDetails.subtitle'),
    complete: false,
    skippable: false,
  },
  [RouteNames.USER.INDEX]: {
    name: RouteNames.USER.INDEX,
    title: () => i18n.t('data.onboarding.steps.ownersStepOne.title'),
    subtitle: () => i18n.t('data.onboarding.steps.ownersStepOne.subtitle'),
    subtitle2: () => ({
      title: i18n.t('data.onboarding.steps.ownersStepOne.subtitle2.title'),
      link: i18n.t('data.onboarding.steps.ownersStepOne.subtitle2.link'),
      href: i18n.t('data.onboarding.steps.ownersStepOne.subtitle2.href'),
    }),
    nextRoute: { name: RouteNames.OWNERS.INDEX },
    complete: false,
    skippable: false,
    firstStepOfForm: { name: RouteNames.USER.INDEX },
    formCompleteStep: { name: RouteNames.OWNERS.INDEX },
  },

  [RouteNames.OWNERS.INDEX]: {
    name: RouteNames.OWNERS.INDEX,
    title: () => i18n.t('data.onboarding.steps.ownersStepOne.title'),
    subtitle: () => i18n.t('data.onboarding.steps.ownersStepOne.subtitle'),
    subtitle2: () => ({
      title: i18n.t('data.onboarding.steps.ownersStepOne.subtitle2.title'),
      link: i18n.t('data.onboarding.steps.ownersStepOne.subtitle2.link'),
      href: i18n.t('data.onboarding.steps.ownersStepOne.subtitle2.href'),
    }),
    ctaText: i18n.t('common.buttons.next'),
    nextRoute: { name: RouteNames.PROFILE.INDEX },
    redirectRoute: { name: RouteNames.USER.INDEX },
    complete: false,
    firstStepOfForm: { name: RouteNames.USER.INDEX },
    formCompleteStep: { name: RouteNames.OWNERS.INDEX },
    skippable: false,
  },
};

export const PAYMENTS_CONFIG: PaymentsConfig = {
  [PAYMENTS_STATE_MODULE]: {
    steps: PAYMENTS_STEPS,
  },
};

export const PAYMENTS_ACTIONS: PaymentsActions = {
  [PAYMENTS_STATE_MODULE]: {
    UPDATE_ALL_PAYMENTS_BIZ_PROFILE_STEPS:
      'UPDATE_ALL_PAYMENTS_BIZ_PROFILE_STEPS',
    UPDATE_ALL_PAYMENTS_ONBOARDING_STEPS:
      'UPDATE_ALL_PAYMENTS_ONBOARDING_STEPS',
    UPDATE_PAYMENTS_ONBOARDING_STEP: 'UPDATE_PAYMENTS_ONBOARDING_STEP',
    UPDATE_PAYMENTS_BIZ_PROFILE_STEP: 'UPDATE_PAYMENTS_BIZ_PROFILE_STEP',
    RESET_SELECTED_BILL: 'RESET_SELECTED_BILL',
    UPDATE_STEP: 'UPDATE_PAYMENTS_STEP',
  },
};

export const PAYMENTS_TRACKING_PARAMS = [
  'utm_campaign',
  'utm_content',
  'utm_source',
  'utm_medium',
  'utm_term',
  'utm_id',
  'partner_key',
  'sub_id',
  'campaign_id',
  'gclid',
  'amplitude_id',
  'cb-ex',
];

export enum BNPL_TYPES {
  LOAN = 'Loan',
  CAPPED_IRR_MCA = 'Capped IRR MCA',
}

export const PAYMENT_PROFILE_NOT_FOUND = 'payments profile not found';

export const PAY_OUT_CURRENCY_OPTIONS: PayOutCurrencyOptions = {
  USD: {
    symbol: '$',
    name: 'US Dollar',
    symbol_native: '$',
    decimal_digits: 2,
    rounding: 0,
    code: 'USD',
    name_plural: 'US dollars',
  },
  CAD: {
    symbol: 'CA$',
    name: 'Canadian Dollar',
    symbol_native: '$',
    decimal_digits: 2,
    rounding: 0,
    code: 'CAD',
    name_plural: 'Canadian dollars',
  },
  GBP: {
    symbol: '£',
    name: 'British Pound Sterling',
    symbol_native: '£',
    decimal_digits: 2,
    rounding: 0,
    code: 'GBP',
    name_plural: 'British pounds sterling',
  },
  EUR: {
    symbol: '€',
    name: 'Euro',
    symbol_native: '€',
    decimal_digits: 2,
    rounding: 0,
    code: 'EUR',
    name_plural: 'euros',
  },
  AUD: {
    symbol: 'AU$',
    name: 'Australian Dollar',
    symbol_native: '$',
    decimal_digits: 2,
    rounding: 0,
    code: 'AUD',
    name_plural: 'Australian dollars',
  },
};

export const DEFAULT_CURRENCY = 'USD';
export function getPaymentsRevenueBands(currency = ''): RevenueBand[] {
  return [
    { name: () => `${currency}0-${currency}10K`, value: '0-10k' },
    { name: () => `${currency}10K-${currency}20K`, value: '10k-20k' },
    { name: () => `${currency}20K-${currency}50K`, value: '20k-50k' },
    { name: () => `${currency}50K-${currency}100K`, value: '50k-100k' },
    { name: () => `${currency}100K-${currency}250K`, value: '100k-250k' },
    { name: () => `${currency}250K-${currency}500K`, value: '250k-500k' },
    { name: () => `${currency}500K-${currency}1M`, value: '500k-1m' },
    { name: () => `${currency}1M-${currency}5M`, value: '1m-5m' },
    { name: () => `${currency}5M+`, value: '5m+' },
  ];
}
export enum V2_CAPACITY_ERROR_TYPES {
  CAPACITY_DISQUALIFIED = 'capacity_disqualified',
  DISCONNECTED_ECOM = 'disconnected_ecom',
  CAPACITY_NOT_FOUND = 'capacity_not_found',
}

export enum PAY_IN_PAY_OUT_STATUS {
  UNREAD = 'unread',
  ERROR = 'error',
  CANCELLED = 'cancelled',
  PROCESSING = 'processing',
  IN_DILIGENCE = 'in_diligence',
  DILIGENCE_PASSED = 'diligence_passed',
  DILIGENCE_FAILED = 'diligence_failed',
  CONTRACT_SIGNED = 'contract_signed',
  PAYMENT_PENDING = 'payment_pending',
  PAYMENT_COMPLETED = 'payment_completed',
  AUTO_DEBITING_ACTIVE = 'auto_debiting_active',
  ACCOUNT_PENDING = 'account_pending',
  CAPITAL_RECOUPED = 'capital_recouped',
}

export enum PaymentStatus {
  READY_TO_SEND = 'ready_to_send',
  RELEASED = 'released',
  SUBMITTED = 'submitted',
  COMPLETED = 'completed',
  FAILED = 'failed',
  DELETED = 'deleted',
}

export enum ChargeType {
  OURS = 'ours',
  SHARED = 'shared',
}

export const PAY_IN_PAY_OUT_STATUS_LOCKED_BY_DILIGENCE = [
  PAY_IN_PAY_OUT_STATUS.IN_DILIGENCE,
  PAY_IN_PAY_OUT_STATUS.DILIGENCE_PASSED,
  PAY_IN_PAY_OUT_STATUS.DILIGENCE_FAILED,
  PAY_IN_PAY_OUT_STATUS.CONTRACT_SIGNED,
  PAY_IN_PAY_OUT_STATUS.PAYMENT_PENDING,
  PAY_IN_PAY_OUT_STATUS.PAYMENT_COMPLETED,
  PAY_IN_PAY_OUT_STATUS.AUTO_DEBITING_ACTIVE,
  PAY_IN_PAY_OUT_STATUS.CAPITAL_RECOUPED,
];

export enum BNPL_USER_CATEGORIES {
  NET_NEW = 'net_new',
  ACTIVE_VALID_ADVANCE = 'active_valid_advance',
  CLOSED_LOST_VALID_ADVANCE = 'closed_lost_valid_advance',
  PAST_VALID_ADVANCE_ONLY = 'past_valid_advance_only',
}

const WAITLIST_BANNER_MESSAGING: WaitlistBannerMessaging = {
  [BNPL_USER_CATEGORIES.NET_NEW]: {
    [COUNTRY_CODES.CA]: {
      title: 'paymentsPage.waitlistBanner.netNew.ca.title',
      paragraph1: 'paymentsPage.waitlistBanner.netNew.ca.paragraph1',
      paragraph2: 'paymentsPage.waitlistBanner.netNew.ca.paragraph2',
    },
    INTERNATIONAL: {
      title: 'paymentsPage.waitlistBanner.netNew.international.title',
      paragraph1: 'paymentsPage.waitlistBanner.netNew.international.paragraph1',
      linkText: 'paymentsPage.waitlistBanner.netNew.international.linkText',
      linkUrl:
        'https://help.clear.co/s/article/Invoice-Funding-Eligibility-Requirements?language=en_US',
    },
  },
  [BNPL_USER_CATEGORIES.ACTIVE_VALID_ADVANCE]: {
    [COUNTRY_CODES.US]: {
      title: 'paymentsPage.waitlistBanner.activeValidAdvance.us.title',
      paragraph1:
        'paymentsPage.waitlistBanner.activeValidAdvance.us.paragraph1',
    },
    [COUNTRY_CODES.CA]: {
      title: 'paymentsPage.waitlistBanner.activeValidAdvance.ca.title',
      paragraph1:
        'paymentsPage.waitlistBanner.activeValidAdvance.ca.paragraph1',
      paragraph2:
        'paymentsPage.waitlistBanner.activeValidAdvance.ca.paragraph2',
      paragraph3:
        'paymentsPage.waitlistBanner.activeValidAdvance.ca.paragraph3',
      p2LinkText:
        'paymentsPage.waitlistBanner.activeValidAdvance.ca.p2LinkText',
      paragraph2NoLink:
        'paymentsPage.waitlistBanner.activeValidAdvance.ca.paragraph2NoLink',
      supportEmail:
        'paymentsPage.waitlistBanner.activeValidAdvance.ca.supportEmail',
    },
  },
  [BNPL_USER_CATEGORIES.CLOSED_LOST_VALID_ADVANCE]: {
    [COUNTRY_CODES.US]: {
      title: 'paymentsPage.waitlistBanner.closedLostValidAdvance.us.title',
      paragraph1:
        'paymentsPage.waitlistBanner.closedLostValidAdvance.us.paragraph1',
    },
    [COUNTRY_CODES.CA]: {
      title: 'paymentsPage.waitlistBanner.closedLostValidAdvance.ca.title',
      paragraph1:
        'paymentsPage.waitlistBanner.closedLostValidAdvance.ca.paragraph1',
      paragraph2:
        'paymentsPage.waitlistBanner.closedLostValidAdvance.ca.paragraph2',
    },
    INTERNATIONAL: {
      title: 'paymentsPage.waitlistBanner.netNew.international.title',
      paragraph1: 'paymentsPage.waitlistBanner.netNew.international.paragraph1',
      linkText: 'paymentsPage.waitlistBanner.netNew.international.linkText',
      linkUrl:
        'https://help.clear.co/s/article/Invoice-Funding-Eligibility-Requirements?language=en_US',
    },
  },
  [BNPL_USER_CATEGORIES.PAST_VALID_ADVANCE_ONLY]: {
    [COUNTRY_CODES.US]: {
      title: 'paymentsPage.waitlistBanner.pastValidAdvanceOnly.us.title',
      paragraph1:
        'paymentsPage.waitlistBanner.pastValidAdvanceOnly.us.paragraph1',
    },
    [COUNTRY_CODES.CA]: {
      title: 'paymentsPage.waitlistBanner.pastValidAdvanceOnly.ca.title',
      paragraph1:
        'paymentsPage.waitlistBanner.pastValidAdvanceOnly.ca.paragraph1',
      paragraph2:
        'paymentsPage.waitlistBanner.pastValidAdvanceOnly.ca.paragraph2',
    },
    INTERNATIONAL: {
      title: 'paymentsPage.waitlistBanner.netNew.international.title',
      paragraph1: 'paymentsPage.waitlistBanner.netNew.international.paragraph1',
      linkText: 'paymentsPage.waitlistBanner.netNew.international.linkText',
      linkUrl:
        'https://help.clear.co/s/article/Invoice-Funding-Eligibility-Requirements?language=en_US',
    },
  },
};

export enum PAYMENT_SCHEME_TYPES {
  LOCAL = 'local',
  REGULAR = 'regular', // to deprecate once update in bene service
  PRIORITY = 'priority', // equivalent to wire transfer
}

export function waitlistBannerText(
  userCategory: BNPL_USER_CATEGORIES,
  countryCode: CountryCode,
  forceBnplFlag = false,
  hasTakenBnplAdvance = false,
): BannerMessage | undefined {
  // No country code --> user is prob logged out --> no banner
  if (!countryCode || forceBnplFlag || hasTakenBnplAdvance) return undefined;
  else if ([COUNTRY_CODES.US, COUNTRY_CODES.CA].includes(countryCode)) {
    return WAITLIST_BANNER_MESSAGING[userCategory][countryCode];
  }
  return WAITLIST_BANNER_MESSAGING[userCategory].INTERNATIONAL;
}
